import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { api, supabase } from '../lib/api';
import {
  needsEmailConfirmation,
  sendEmailConfirmation,
  EMAIL_CONFIRMATION_RESEND_COOLDOWN_MS,
} from '../lib/emailConfirmation';
import { useToast } from '../components/ui/Toast';
import * as FiIcons from 'react-icons/fi';
import { MdOutlineInsights } from 'react-icons/md';
import SafeIcon from '../common/SafeIcon';
import GlobalSearchBar from '../components/ui/GlobalSearchBar';
import { useVisiblePolling } from '../lib/useVisiblePolling';

const {
  FiHome, FiUsers, FiTarget, FiKanban, FiSettings,
  FiLogOut, FiMenu, FiShield, FiUser, FiX, FiTool, FiHeadphones,
  FiMessageSquare, FiCalendar, FiSend, FiBarChart2, FiFilter,
  FiChevronLeft, FiChevronRight, FiChevronDown, FiFileText, FiPieChart,
  FiAward, FiBookOpen, FiBell, FiClock,
} = FiIcons;

const SIDEBAR_COLLAPSED_KEY = 'prosperity_sidebar_collapsed';
const SIDEBAR_GROUPS_KEY = 'prosperity_sidebar_groups';
const EMAIL_CONFIRM_BANNER_DISMISS_KEY = 'pl_email_confirm_banner_dismissed';

const ADMIN_ROLES = ['super_admin'];
const CAMPAIGN_ROLES = ['admin', 'manager', 'super_admin'];
const TEMPLATE_ADMIN_ROLES = ['admin', 'super_admin'];
const MARKETING_INTELLIGENCE_ROLES = ['admin', 'super_admin'];
const MANAGEMENT_ROLES = ['admin', 'manager', 'super_admin'];

/** Sidebar links visible to Associates. Route access is unchanged. */
const ASSOCIATE_SIDEBAR_PATHS = new Set([
  '/dashboard',
  '/leads',
  '/toolkit',
  '/mentors',
  '/profile',
  '/settings',
  '/academy-hub',
  '/launch-toolkit',
  '/top25',
  '/reminders',
]);

const DEFAULT_GROUP_STATE = {
  prosperityPipeline: true,
  leadsAndPipeline: true,
  communication: true,
  resources: true,
  marketing: true,
  business: true,
};

function readGroupState() {
  try {
    const raw = localStorage.getItem(SIDEBAR_GROUPS_KEY);
    if (!raw) return { ...DEFAULT_GROUP_STATE };
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_GROUP_STATE, ...parsed };
  } catch {
    return { ...DEFAULT_GROUP_STATE };
  }
}

function writeGroupState(state) {
  try {
    localStorage.setItem(SIDEBAR_GROUPS_KEY, JSON.stringify(state));
  } catch {
    /* ignore storage errors */
  }
}

function notificationTimeLabel(iso) {
  if (!iso) return '';
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true });
  } catch {
    return '';
  }
}

function applyNotificationUnreadDelta(payload, setUnread) {
  const eventType = payload?.eventType;
  const next = payload?.new;
  const prev = payload?.old;
  if (eventType === 'INSERT' && next && !next.read_at) {
    setUnread((n) => n + 1);
    return;
  }
  if (eventType === 'UPDATE' && next) {
    if (prev && Object.prototype.hasOwnProperty.call(prev, 'read_at')) {
      const wasUnread = !prev.read_at;
      const isUnread = !next.read_at;
      if (wasUnread && !isUnread) setUnread((n) => Math.max(0, n - 1));
      else if (!wasUnread && isUnread) setUnread((n) => n + 1);
    } else if (next.read_at) {
      setUnread((n) => Math.max(0, n - 1));
    }
  }
}

function NotificationBell({ userId }) {
  const navigate = useNavigate();
  const wrapRef = useRef(null);
  const openRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [markingAll, setMarkingAll] = useState(false);
  openRef.current = open;

  const fetchCount = useCallback(() => {
    if (!userId) return;
    api.notifications.getUnreadCount().then(setUnread).catch(() => {});
  }, [userId]);

  const fetchList = useCallback(() => {
    if (!userId) return;
    api.notifications.getMine(25).then((rows) => setItems(rows || [])).catch(() => {});
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setItems([]);
      setUnread(0);
      return undefined;
    }
    fetchCount();
    let channel;
    if (supabase) {
      const onChange = (payload) => {
        applyNotificationUnreadDelta(payload, setUnread);
        if (openRef.current) fetchList();
      };
      channel = supabase
        .channel(`header-notifications-${userId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${userId}`,
          },
          onChange,
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${userId}`,
          },
          onChange,
        )
        .subscribe();
    }
    return () => {
      if (channel && supabase) supabase.removeChannel(channel);
    };
  }, [userId, fetchCount, fetchList]);

  useEffect(() => {
    if (open && userId) fetchList();
  }, [open, userId, fetchList]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openNotification = async (row) => {
    setOpen(false);
    if (row?.id && !row.read_at) {
      try {
        await api.notifications.markRead(row.id);
      } catch {
        /* still navigate */
      }
    }
    navigate(row?.link_path || '/reminders');
  };

  const markAll = async () => {
    if (unread <= 0) return;
    setMarkingAll(true);
    try {
      await api.notifications.markAllRead();
      setUnread(0);
      if (openRef.current) fetchList();
    } catch {
      /* ignore */
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        className="relative p-2 text-gray-500 hover:bg-gray-100 active:bg-gray-200 rounded-md touch-manipulation"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((v) => !v)}
      >
        <SafeIcon icon={FiBell} className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute top-0.5 right-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold leading-none text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-[min(calc(100vw-2rem),22rem)] bg-white border border-lineGray rounded-xl shadow-lg overflow-hidden z-[120]">
          <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-b border-lineGray">
            <p className="text-sm font-heading font-bold text-navy">Notifications</p>
            <button
              type="button"
              className="text-xs font-semibold text-primary hover:underline disabled:text-slateText disabled:no-underline disabled:cursor-not-allowed"
              disabled={unread <= 0 || markingAll}
              onClick={markAll}
            >
              {markingAll ? 'Marking…' : 'Mark all read'}
            </button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-3 py-8 text-sm text-center text-slateText">No notifications yet</p>
            ) : (
              items.map((row) => {
                const unreadRow = !row.read_at;
                return (
                  <button
                    key={row.id}
                    type="button"
                    onClick={() => openNotification(row)}
                    className={`w-full text-left px-3 py-2.5 border-b border-[#F0EEE7] last:border-0 hover:bg-ivory ${
                      unreadRow ? 'bg-skyTint/40' : 'bg-white'
                    }`}
                  >
                    <p className={`text-sm leading-snug ${unreadRow ? 'font-bold text-navy' : 'font-medium text-gray-800'}`}>
                      {row.title}
                    </p>
                    <p className="text-[11px] text-slateText mt-0.5">{notificationTimeLabel(row.created_at)}</p>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function isSmsReadRoute(path) {
  if (!path) return false;
  if (path === '/inbox' || path.startsWith('/inbox/')) return true;
  const leadMatch = path.match(/^\/leads\/([^/]+)\/?$/);
  return Boolean(leadMatch && leadMatch[1] !== 'new');
}

function itemMatchesPath(item, pathname) {
  if (item.stub || !item.to) return false;
  if (item.to === '/campaigns') {
    return pathname === '/campaigns' || pathname.startsWith('/campaigns/');
  }
  if (pathname === item.to) return true;
  if (pathname.startsWith(`${item.to}/`)) return true;
  return (item.subItems ?? []).some(
    (sub) => pathname === sub.to || pathname.startsWith(`${sub.to}/`),
  );
}

function NavItemRow({
  item,
  user,
  collapsed,
  isMobileDrawer,
  afterNav,
  mentionCount,
}) {
  const visibleSubItems = (item.subItems ?? []).filter(
    (sub) => !sub.roles || sub.roles.includes(user?.role),
  );

  if (item.stub) {
    const tooltip = collapsed && !isMobileDrawer
      ? `${item.label} — Coming Soon`
      : undefined;
    return (
      <div
        title={tooltip}
        aria-disabled="true"
        className={`relative flex items-center rounded-md text-sm font-medium cursor-not-allowed select-none touch-manipulation opacity-40 border border-lineGray ${
          collapsed && !isMobileDrawer ? 'justify-center px-2 py-2.5' : 'px-3 py-2.5'
        } text-locked`}
      >
        <span className="relative shrink-0">
          <SafeIcon
            icon={item.icon}
            className={`h-5 w-5 ${collapsed && !isMobileDrawer ? '' : 'mr-3'}`}
          />
        </span>
        {(!collapsed || isMobileDrawer) && (
          <>
            <span className="truncate">{item.label}</span>
            <span className="ml-auto shrink-0 text-[9px] font-bold bg-lockedBg text-locked px-1.5 py-0.5 rounded-[100px] uppercase tracking-wide">
              Coming Soon
            </span>
          </>
        )}
      </div>
    );
  }

  return (
    <>
      <NavLink
        to={item.to}
        end={item.to === '/campaigns'}
        title={collapsed && !isMobileDrawer ? item.label : undefined}
        onClick={afterNav}
        className={({ isActive }) =>
          `relative flex items-center rounded-md text-sm font-medium transition-colors touch-manipulation ${
            collapsed && !isMobileDrawer ? 'justify-center px-2 py-2.5' : 'px-3 py-2.5'
          } ${
            isActive
              ? 'bg-primary text-white'
              : 'text-gray-300 hover:bg-sidebarHover hover:text-white'
          }`
        }
      >
        <span className="relative shrink-0">
          <SafeIcon icon={item.icon} className={`h-5 w-5 ${collapsed && !isMobileDrawer ? '' : 'mr-3'}`} />
          {collapsed && !isMobileDrawer && item.label === 'Applications' && mentionCount > 0 && (
            <span className="absolute top-0 right-0 w-2 h-2 rounded-full bg-red-500" />
          )}
          {collapsed && !isMobileDrawer && item.label !== 'Applications' && item.badge != null && item.badge > 0 && (
            <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-blue-500" />
          )}
        </span>
        {(!collapsed || isMobileDrawer) && (
          <>
            <span className="truncate">{item.label}</span>
            {item.label === 'Applications' && mentionCount > 0 && (
              <span className="ml-auto flex-shrink-0 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                {mentionCount > 9 ? '9+' : mentionCount}
              </span>
            )}
            {item.badge != null && item.badge > 0 && (
              <span className="ml-auto flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded-full bg-blue-600 px-1 text-[9px] font-bold leading-none text-white">
                {item.badge > 99 ? '99+' : item.badge}
              </span>
            )}
            {item.to === '/admin' && (
              <span className="ml-auto text-[9px] font-bold bg-red-500 text-white px-1.5 py-0.5 rounded-full uppercase tracking-wide">
                SA
              </span>
            )}
          </>
        )}
      </NavLink>
      {(!collapsed || isMobileDrawer) &&
        visibleSubItems.map((sub) => (
          <NavLink
            key={sub.to}
            to={sub.to}
            onClick={afterNav}
            className={({ isActive }) =>
              `flex items-center rounded-md text-sm font-medium transition-colors touch-manipulation ml-6 mr-1 px-3 py-2 ${
                isActive
                  ? 'bg-sidebarHover text-white'
                  : 'text-gray-400 hover:bg-sidebarHover hover:text-white'
              }`
            }
          >
            <span className="truncate">{sub.label}</span>
          </NavLink>
        ))}
    </>
  );
}

function AppSidebarContent({
  user,
  navGroups,
  handleLogout,
  onRequestClose,
  collapsed = false,
  onToggleCollapse,
  mentionCount = 0,
  groupExpanded,
  onToggleGroup,
}) {
  const afterNav = onRequestClose ?? undefined;
  const isMobileDrawer = Boolean(onRequestClose);
  const showLabels = !collapsed || isMobileDrawer;

  return (
    <>
      <div
        className={`h-16 flex items-center border-b border-navy shrink-0 gap-2 transition-all duration-300 ${
          collapsed && !isMobileDrawer ? 'px-2 justify-center' : onRequestClose ? 'px-4 sm:px-6' : 'px-4'
        }`}
      >
        {onRequestClose && (
          <button
            type="button"
            className="p-2 -ml-1 rounded-md text-gray-300 hover:bg-sidebarHover hover:text-white touch-manipulation shrink-0"
            onClick={onRequestClose}
            aria-label="Close menu"
          >
            <SafeIcon icon={FiX} className="h-6 w-6" />
          </button>
        )}
        <div
          className={`text-xl font-heading font-bold text-primary flex items-center min-w-0 ${
            collapsed && !isMobileDrawer ? 'justify-center' : 'gap-2 flex-1'
          }`}
        >
          <div className="w-6 h-6 rounded bg-primary shrink-0" />
          {showLabels && (
            <span className="truncate">Prosperity Leads</span>
          )}
        </div>
        {!isMobileDrawer && onToggleCollapse && !collapsed && (
          <button
            type="button"
            onClick={onToggleCollapse}
            className="p-1.5 rounded-md text-gray-400 hover:bg-sidebarHover hover:text-white shrink-0"
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
          >
            <SafeIcon icon={FiChevronLeft} className="h-5 w-5" />
          </button>
        )}
      </div>
      <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] min-h-0">
        {(() => {
          const visibleGroups = navGroups.filter((g) => g.items.length > 0);
          return visibleGroups.map((group, visibleIndex) => {
            const isCollapsible = Boolean(group.id);
            const isExpanded = !isCollapsible || groupExpanded[group.id] !== false;
            // Dividers only between collapsible group icon blocks in collapsed mode —
            // never before Dashboard, never before the pinned Account section (id: null).
            const showDivider =
              collapsed &&
              !isMobileDrawer &&
              visibleIndex > 0 &&
              isCollapsible;

            return (
              <div key={group.id ?? `ungrouped-${visibleIndex}`} className="space-y-1">
                {showDivider && (
                  <div className="mx-2 my-2 border-t border-navy" aria-hidden="true" />
                )}

                {showLabels && isCollapsible && (
                  <button
                    type="button"
                    onClick={() => onToggleGroup(group.id)}
                    className="w-full flex items-center justify-between px-3 py-1.5 rounded-md text-[10px] font-heading font-bold uppercase tracking-wider text-slateText hover:text-gray-300 hover:bg-sidebarHover/50 transition-colors touch-manipulation"
                    aria-expanded={isExpanded}
                  >
                    <span className="truncate">{group.label}</span>
                    <SafeIcon
                      icon={isExpanded ? FiChevronDown : FiChevronRight}
                      className="h-3.5 w-3.5 shrink-0 ml-1"
                    />
                  </button>
                )}

                <div
                  className={
                    showLabels && isCollapsible
                      ? `overflow-hidden transition-[max-height,opacity] duration-200 ease-in-out ${
                          isExpanded ? 'max-h-[480px] opacity-100' : 'max-h-0 opacity-0'
                        }`
                      : undefined
                  }
                >
                  <div className="space-y-1">
                    {group.items.map((item) => (
                      <NavItemRow
                        key={item.stub ? `stub-${item.label}` : `${group.id ?? 'top'}-${item.to}`}
                        item={item}
                        user={user}
                        collapsed={collapsed}
                        isMobileDrawer={isMobileDrawer}
                        afterNav={afterNav}
                        mentionCount={mentionCount}
                      />
                    ))}
                  </div>
                </div>
              </div>
            );
          });
        })()}
      </nav>

      {!isMobileDrawer && onToggleCollapse && collapsed && (
        <div className="px-2 pb-2 shrink-0">
          <button
            type="button"
            onClick={onToggleCollapse}
            className="w-full flex items-center justify-center py-2 rounded-md text-gray-400 hover:bg-sidebarHover hover:text-white transition-colors"
            aria-label="Expand sidebar"
            title="Expand sidebar"
          >
            <SafeIcon icon={FiChevronRight} className="h-5 w-5" />
          </button>
        </div>
      )}

      <div className={`border-t border-navy shrink-0 ${collapsed && !isMobileDrawer ? 'p-2' : 'p-4'}`}>
        <div className={`flex items-center mb-4 ${collapsed && !isMobileDrawer ? 'justify-center' : 'gap-3'}`}>
          <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-sm font-bold shrink-0">
            {user?.full_name?.charAt(0) || 'U'}
          </div>
          {showLabels && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
              <p className="text-xs text-slateText capitalize">{user?.role}</p>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            afterNav?.();
            handleLogout();
          }}
          title={collapsed && !isMobileDrawer ? 'Logout' : undefined}
          className={`w-full flex items-center text-sm font-medium text-gray-400 rounded-md hover:bg-sidebarHover hover:text-white transition-colors touch-manipulation ${
            collapsed && !isMobileDrawer ? 'justify-center px-2 py-2' : 'px-3 py-2'
          }`}
        >
          <SafeIcon icon={FiLogOut} className={`h-5 w-5 ${collapsed && !isMobileDrawer ? '' : 'mr-3'}`} />
          {showLabels && 'Logout'}
        </button>
      </div>
    </>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [fnaCollapseOverride, setFnaCollapseOverride] = useState(null);
  const [groupExpanded, setGroupExpanded] = useState(readGroupState);
  const [unreadSmsCount, setUnreadSmsCount] = useState(0);
  const [mentionCount, setMentionCount] = useState(0);
  const [pendingExclusivityCount, setPendingExclusivityCount] = useState(0);
  const [pendingTransferCount, setPendingTransferCount] = useState(0);
  const prevPathRef = useRef(location.pathname);
  const [emailBannerDismissed, setEmailBannerDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(EMAIL_CONFIRM_BANNER_DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [emailResending, setEmailResending] = useState(false);
  const [emailCooldownUntil, setEmailCooldownUntil] = useState(0);
  const [emailCooldownLeft, setEmailCooldownLeft] = useState(0);

  const isFullBleed =
    location.pathname === '/inbox' ||
    location.pathname.startsWith('/inbox/') ||
    location.pathname === '/workspace' ||
    location.pathname.startsWith('/workspace/') ||
    /^\/funnels\/[^/]+$/.test(location.pathname);

  const showEmailConfirmBanner =
    needsEmailConfirmation(user) && !emailBannerDismissed && !isFullBleed;

  const hideTopNav =
    location.pathname === '/workspace' ||
    location.pathname.startsWith('/workspace/');

  const isFnaDetailRoute = /^\/financial-analysis\/[^/]+(\/report)?\/?$/.test(location.pathname);

  // Route-derived override: never persisted to localStorage.
  const effectiveCollapsed = isFnaDetailRoute
    ? (fnaCollapseOverride ?? true)
    : sidebarCollapsed;

  useEffect(() => {
    if (!isFnaDetailRoute) setFnaCollapseOverride(null);
  }, [isFnaDetailRoute]);

  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);

  const toggleSidebarCollapsed = useCallback(() => {
    if (isFnaDetailRoute) {
      setFnaCollapseOverride((prev) => !(prev ?? true));
      return;
    }
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {
        /* ignore storage errors */
      }
      return next;
    });
  }, [isFnaDetailRoute]);

  const toggleGroup = useCallback((groupId) => {
    setGroupExpanded((prev) => {
      const next = { ...prev, [groupId]: !prev[groupId] };
      writeGroupState(next);
      return next;
    });
  }, []);

  const handleLogout = async () => {
    try {
      sessionStorage.removeItem(EMAIL_CONFIRM_BANNER_DISMISS_KEY);
    } catch {
      /* ignore */
    }
    await logout();
    navigate('/login');
  };

  useEffect(() => {
    if (!emailCooldownUntil) {
      setEmailCooldownLeft(0);
      return undefined;
    }
    const tick = () => {
      const left = Math.max(0, Math.ceil((emailCooldownUntil - Date.now()) / 1000));
      setEmailCooldownLeft(left);
      if (left <= 0) setEmailCooldownUntil(0);
    };
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [emailCooldownUntil]);

  const dismissEmailBanner = () => {
    setEmailBannerDismissed(true);
    try {
      sessionStorage.setItem(EMAIL_CONFIRM_BANNER_DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  const handleResendEmailConfirmation = async () => {
    if (emailResending || emailCooldownLeft > 0) return;
    setEmailResending(true);
    try {
      const result = await sendEmailConfirmation();
      if (result?.already_confirmed) {
        addToast({ type: 'success', title: 'Email already confirmed' });
        dismissEmailBanner();
        return;
      }
      addToast({
        type: 'success',
        title: 'Check your inbox',
        message: result?.cooldown
          ? (result.message || 'Please wait a moment before requesting another confirmation email.')
          : 'We sent a confirmation link to your email.',
      });
      setEmailCooldownUntil(Date.now() + EMAIL_CONFIRMATION_RESEND_COOLDOWN_MS);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Could not send confirmation email',
        message: err.message || 'Try again in a moment.',
      });
    } finally {
      setEmailResending(false);
    }
  };

  /* Fetch unread SMS count + Realtime subscription */
  useEffect(() => {
    const refreshCount = () =>
      api.smsMessages.getUnreadCount()
        .then(c => setUnreadSmsCount(c))
        .catch(() => {});

    refreshCount();

    window.addEventListener('sms-unread-change', refreshCount);
    window.addEventListener('sms-read-changed', refreshCount);

    let debounceTimer = null;
    const scheduleRefresh = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        refreshCount();
      }, 1500);
    };

    let channel;
    const agencyId = user?.agency_id;
    if (supabase && agencyId) {
      channel = supabase
        .channel('sidebar-unread')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'sms_messages',
            filter: `agency_id=eq.${agencyId}`,
          },
          (payload) => {
            if (payload?.new?.direction === 'inbound') {
              scheduleRefresh();
            }
          },
        )
        .subscribe();
    }

    return () => {
      window.removeEventListener('sms-unread-change', refreshCount);
      window.removeEventListener('sms-read-changed', refreshCount);
      if (debounceTimer) clearTimeout(debounceTimer);
      if (channel && supabase) supabase.removeChannel(channel);
    };
  }, [user?.agency_id]);

  useEffect(() => {
    const prev = prevPathRef.current;
    const next = location.pathname;
    prevPathRef.current = next;
    if (isSmsReadRoute(prev) && !isSmsReadRoute(next)) {
      api.smsMessages.getUnreadCount()
        .then((c) => setUnreadSmsCount(c))
        .catch(() => {});
    }
  }, [location.pathname]);

  const refreshSidebarBadges = useCallback(async () => {
    if (!user) {
      setMentionCount(0);
      setPendingExclusivityCount(0);
      setPendingTransferCount(0);
      return;
    }
    try {
      const counts = await api.badges.getSidebarCounts();
      setMentionCount(counts.mentionsUnread);
      setPendingExclusivityCount(counts.exclusivityPending);
      setPendingTransferCount(counts.transfersPending);
    } catch {
      // Silent fail — badges are non-critical
    }
  }, [user]);

  useEffect(() => {
    refreshSidebarBadges();
  }, [refreshSidebarBadges]);

  useVisiblePolling(refreshSidebarBadges, 120000, Boolean(user));

  useEffect(() => {
    const onTransferChange = () => { refreshSidebarBadges(); };
    window.addEventListener('transfer-pending-change', onTransferChange);
    return () => window.removeEventListener('transfer-pending-change', onTransferChange);
  }, [refreshSidebarBadges]);

  useEffect(() => {
    if (location.pathname.startsWith('/applications/') &&
        location.pathname !== '/applications/reports') {
      // Delay to allow markMentionsRead to complete
      const timeout = setTimeout(() => {
        refreshSidebarBadges();
      }, 1500);
      return () => clearTimeout(timeout);
    }
  }, [location.pathname, refreshSidebarBadges]);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeMobileNav();
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [mobileNavOpen, closeMobileNav]);

  const settingsBadge =
    (MANAGEMENT_ROLES.includes(user?.role) ? pendingExclusivityCount : 0) + pendingTransferCount;

  const filterByRole = (item) => !item.roles || item.roles.includes(user?.role);

  const navGroups = useMemo(() => {
    const groups = [
      {
        id: null,
        label: null,
        items: [
          { to: '/dashboard', icon: FiHome, label: 'Dashboard', roles: null },
          { to: '/reminders', icon: FiClock, label: 'Reminders', roles: null },
        ].filter(filterByRole),
      },
      {
        id: 'prosperityPipeline',
        label: 'Prosperity Pipeline',
        items: [
          { to: '/academy-hub', icon: FiBookOpen, label: 'Academy Hub', roles: null },
          { to: '/launch-toolkit', icon: FiCalendar, label: 'Launch Toolkit', roles: null },
          { to: '/top25', icon: FiUsers, label: 'Top 25 List', roles: null },
        ].filter(filterByRole),
      },
      {
        id: 'leadsAndPipeline',
        label: 'Leads & Pipeline',
        items: [
          { to: '/leads', icon: FiUsers, label: 'Leads', roles: null },
          { to: '/recruits', icon: FiTarget, label: 'Recruits', roles: null },
          { to: '/pipeline', icon: FiKanban, label: 'Pipeline', roles: null },
        ].filter(filterByRole),
      },
      {
        id: 'communication',
        label: 'Communication',
        items: [
          { to: '/workspace', icon: FiHeadphones, label: 'Workspace', roles: null },
          { to: '/inbox', icon: FiMessageSquare, label: 'Inbox', roles: null, badge: unreadSmsCount },
          { to: '/calendar', icon: FiCalendar, label: 'Calendar', roles: null },
        ].filter(filterByRole),
      },
      {
        id: 'resources',
        label: 'Resources',
        items: [
          { to: '/toolkit', icon: FiTool, label: 'Toolkit', roles: null },
          { to: '/financial-analysis', icon: FiPieChart, label: 'Financial Analysis', roles: null },
        ].filter(filterByRole),
      },
      {
        id: 'marketing',
        label: 'Marketing',
        items: [
          { to: '/funnels', icon: FiFilter, label: 'Funnels', roles: null },
          { to: '/mentors', icon: FiAward, label: 'Academy', roles: null },
          {
            to: '/campaigns',
            icon: FiSend,
            label: 'Campaigns',
            roles: CAMPAIGN_ROLES,
            subItems: [
              { to: '/campaigns/templates', label: 'Templates', roles: TEMPLATE_ADMIN_ROLES },
            ],
          },
          {
            to: '/marketing-intelligence',
            icon: MdOutlineInsights,
            label: 'Marketing Intelligence',
            roles: MARKETING_INTELLIGENCE_ROLES,
          },
        ].filter(filterByRole),
      },
      {
        id: 'business',
        label: 'Business',
        items: [
          { to: '/applications', icon: FiFileText, label: 'Applications', roles: null },
          { to: '/financial-analysis', icon: FiPieChart, label: 'Financial Analysis', roles: null },
          { to: '/tracker', icon: FiBarChart2, label: 'Tracker', roles: null },
        ].filter(filterByRole),
      },
      {
        id: null,
        label: null,
        items: [
          { to: '/profile', icon: FiUser, label: 'My Profile', roles: null },
          {
            to: '/settings',
            icon: FiSettings,
            label: 'Settings',
            roles: null,
            badge: settingsBadge,
          },
          { to: '/admin', icon: FiShield, label: 'Admin', roles: ADMIN_ROLES },
        ].filter(filterByRole),
      },
    ];

    if (user?.role === 'associate') {
      return groups.map((group) => {
        if (group.id === 'prosperityPipeline') return group;
        return {
          ...group,
          items: group.items.filter((item) => item.to && ASSOCIATE_SIDEBAR_PATHS.has(item.to)),
        };
      });
    }

    return groups;
  }, [user?.role, unreadSmsCount, settingsBadge]);

  /* Auto-expand groups that contain the active route */
  useEffect(() => {
    const pathname = location.pathname;
    const activeGroupIds = navGroups
      .filter((g) => g.id && g.items.some((item) => itemMatchesPath(item, pathname)))
      .map((g) => g.id);

    if (activeGroupIds.length === 0) return;

    setGroupExpanded((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const id of activeGroupIds) {
        if (!next[id]) {
          next[id] = true;
          changed = true;
        }
      }
      if (changed) writeGroupState(next);
      return changed ? next : prev;
    });
  }, [location.pathname, navGroups]);

  return (
    <div className="flex h-screen bg-ivory overflow-hidden">
      <aside
        className={`bg-navyDeep text-white flex-col hidden md:flex flex-shrink-0 min-h-0 transition-[width] duration-300 ease-in-out overflow-hidden ${
          effectiveCollapsed ? 'w-16' : 'w-64'
        }`}
      >
        <AppSidebarContent
          user={user}
          navGroups={navGroups}
          handleLogout={handleLogout}
          collapsed={effectiveCollapsed}
          onToggleCollapse={toggleSidebarCollapsed}
          mentionCount={mentionCount}
          groupExpanded={groupExpanded}
          onToggleGroup={toggleGroup}
        />
      </aside>

      <main className="flex-1 flex flex-col overflow-hidden min-w-0 transition-all duration-300 ease-in-out">
        {!hideTopNav && (
          <>
            <header className="h-16 bg-white border-b border-lineGray flex items-center justify-between px-4 md:px-6 flex-shrink-0 gap-4 relative z-[110] md:z-10">
              <div className="text-lg font-heading font-bold text-primary md:hidden truncate min-w-0">Prosperity Leads</div>
              <div className="hidden md:flex flex-1 max-w-sm">
                <GlobalSearchBar />
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <NotificationBell userId={user?.id} />
                <div className="hidden md:flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-sm font-bold text-white">
                    {user?.full_name?.charAt(0) || 'U'}
                  </div>
                  <div className="hidden lg:block">
                    <p className="text-sm font-medium text-gray-900 leading-none">{user?.full_name}</p>
                    <p className="text-xs text-slateText capitalize mt-0.5">{user?.role}</p>
                  </div>
                </div>
                <button
                  type="button"
                  className="p-2 text-gray-500 hover:bg-gray-100 active:bg-gray-200 rounded-md md:hidden touch-manipulation"
                  aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
                  aria-expanded={mobileNavOpen}
                  aria-controls="mobile-app-sidebar"
                  onClick={() => setMobileNavOpen((open) => !open)}
                >
                  <SafeIcon icon={FiMenu} className="h-6 w-6 pointer-events-none" />
                </button>
              </div>
            </header>

            <div className="md:hidden px-4 py-2 bg-white border-b border-lineGray">
              <GlobalSearchBar />
            </div>
          </>
        )}

        {hideTopNav && (
          <div className="md:hidden h-12 bg-white border-b border-lineGray flex items-center justify-end px-4 flex-shrink-0 gap-1">
            <NotificationBell userId={user?.id} />
            <button
              type="button"
              className="p-2 text-gray-500 hover:bg-gray-100 active:bg-gray-200 rounded-md touch-manipulation"
              aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileNavOpen}
              aria-controls="mobile-app-sidebar"
              onClick={() => setMobileNavOpen((open) => !open)}
            >
              <SafeIcon icon={FiMenu} className="h-6 w-6 pointer-events-none" />
            </button>
          </div>
        )}

        <div
          className={`flex-1 min-h-0 ${
            isFullBleed ? 'overflow-hidden' : 'overflow-auto bg-ivory p-4 md:p-8'
          }`}
        >
          {showEmailConfirmBanner && (
            <div className="shrink-0 mb-4 flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <div className="flex-1 min-w-0">
                <p className="font-medium">Confirm your email address</p>
                <p className="mt-0.5 text-amber-800/90">
                  Please confirm your email so we know this account belongs to you.
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    disabled={emailResending || emailCooldownLeft > 0}
                    onClick={() => void handleResendEmailConfirmation()}
                    className="inline-flex items-center rounded-md bg-amber-800 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-900 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {emailResending
                      ? 'Sending…'
                      : emailCooldownLeft > 0
                        ? `Resend in ${emailCooldownLeft}s`
                        : 'Resend confirmation email'}
                  </button>
                </div>
              </div>
              <button
                type="button"
                onClick={dismissEmailBanner}
                className="p-1 rounded-md text-amber-700 hover:bg-amber-100 shrink-0"
                aria-label="Dismiss email confirmation banner"
              >
                <SafeIcon icon={FiX} className="h-4 w-4" />
              </button>
            </div>
          )}
          <div className={isFullBleed ? 'flex h-full min-h-0 flex-col' : 'w-full'}>
            <Outlet />
          </div>
        </div>
      </main>

      {/* Mobile drawer + backdrop: sibling after main so it stacks above; pointer-events toggled for iOS */}
      <div
        className={`md:hidden fixed inset-0 z-[100] ${mobileNavOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}
        aria-hidden={!mobileNavOpen}
      >
        <button
          type="button"
          tabIndex={mobileNavOpen ? 0 : -1}
          className={`absolute inset-0 z-0 bg-black/50 touch-manipulation border-0 p-0 appearance-none transition-opacity duration-200 ${
            mobileNavOpen ? 'opacity-100' : 'opacity-0'
          }`}
          aria-label="Close menu"
          onClick={closeMobileNav}
        />
        <aside
          id="mobile-app-sidebar"
          role="dialog"
          aria-modal="true"
          aria-label="Main navigation"
          className={`absolute left-0 top-0 z-[1] flex h-full w-[min(100%,16rem)] max-w-[85vw] flex-col bg-navyDeep text-white shadow-lg transition-transform duration-300 ease-out will-change-transform touch-manipulation min-h-0 ${
            mobileNavOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <AppSidebarContent
            user={user}
            navGroups={navGroups}
            handleLogout={handleLogout}
            onRequestClose={closeMobileNav}
            mentionCount={mentionCount}
            groupExpanded={groupExpanded}
            onToggleGroup={toggleGroup}
          />
        </aside>
      </div>
    </div>
  );
}
